import { useContext, useEffect, useMemo, useState } from "react";
import { TranslationContext } from "../../shared/providers/translation/translation.provider";
import { LoadingContext } from "../../shared/providers/loading/loading.provider";
import InputText from "../../components/Forms/InputText/InputText";
import Button from "../../components/Button/Button";
import { AuthenticationContext } from "../../shared/providers/authentication/authentication.provider";
import {
    IPasswordChange,
    IRequestError
} from "../../shared/interfaces/authentication.interface";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useChangePassword } from "../../shared/hooks/useAuthentication";
import { DEFAULT_LANGUAGE } from "../../shared/constants";
import { IUserSettings } from "../../shared/interfaces/user.interface";
import { useSetUserSettings } from "../../shared/hooks/useUser";
import { routes } from "../../shared/router/routes";
import Link from "../../components/Link/Link";

function Account() {
    const { translate, lang } = useContext(TranslationContext);
    const { setIsLoading } = useContext(LoadingContext);
    const [isSuccess, setIsSuccess] = useState<boolean | undefined>();
    const [settingPassword, setSettingPassword] = useState<boolean>(false);
    const { user } = useContext(AuthenticationContext);
    const changePassword = useChangePassword();
    const changeSettings = useSetUserSettings();

    const passwordRequirements = useMemo(
        () => [
            {
                label: translate("common.password.required", "Required"),
                test: (v: string) => !v,
                display: false
            },
            {
                label: translate(
                    "common.password.error_ncharacters",
                    "Between 8 and 64 characters"
                ),
                test: (v: string) => v.length < 8 || v.length > 64,
                display: true
            },
            {
                label: translate("common.password.error_number", "At least one number"),
                test: (v: string) => !/[0-9]/.test(v),
                display: true
            },
            {
                label: translate(
                    "common.password.error_uppercase",
                    "At least one uppercase letter"
                ),
                test: (v: string) => !/[A-Z]/.test(v),
                display: true
            },
            {
                label: translate(
                    "common.password.error_special_character",
                    "At least one special character (!, @, #, $, %, ^, &, *)"
                ),
                test: (v: string) => !/[!@#$%^&*]/.test(v),
                display: true
            },
            {
                label: translate(
                    "common.password.error_lowercase",
                    "At least one lowercase letter"
                ),
                test: (v: string) => !/[a-z]/.test(v),
                display: true
            }
        ],
        [translate]
    );

    const passwordForm = useFormik({
        initialValues: {
            currentPassword: "",
            newPassword: "",
            confirmNewPassword: ""
        },
        validationSchema: Yup.object().shape({
            newPassword: Yup.string().test(
                "password-rules",
                translate("form.password.invalid", "Invalid password"),
                function (val) {
                    const errors: string[] = [];
                    const value = val ?? "";

                    passwordRequirements.forEach((r) => {
                        if (r.test(value)) errors.push(r.label);
                    });

                    if (errors.length) {
                        return this.createError({
                            message: errors.join("/")
                        });
                    }

                    return true;
                }
            ),
            confirmNewPassword: Yup.string()
                .required(
                    translate("form.confirm_password.required", "Confirm password please")
                )
                .test(
                    "confirm-password",
                    translate("form.confirm_password.mismatch", "Passwords do not match"),
                    function (val) {
                        return this.parent.newPassword === val;
                    }
                )
        }),
        validateOnChange: true,
        onSubmit: async (values: IPasswordChange) => {
            setIsLoading(true);
            const response = await changePassword.mutateAsync({
                currentPassword: values.currentPassword,
                newPassword: values.newPassword
            });

            if (response.success) {
                setIsSuccess(true);
                setSettingPassword(false);
                passwordForm.resetForm();
            } else {
                const data = response.data as IRequestError;
                passwordForm.setFieldError(
                    "currentPassword",
                    translate(`auth.code.${data.code}`, data.message)
                );
            }

            setIsLoading(false);
        }
    });

    const communicationSettings = useFormik({
        initialValues: {
            notifyPush: true,
            notifyEmail: false,
            communicationLanguage: lang ?? DEFAULT_LANGUAGE
        },
        validateOnChange: true,
        onSubmit: async (values: IUserSettings) => {
            setIsLoading(true);
            try {
                await changeSettings.mutateAsync({
                    ...values,
                    communicationLanguage: lang ?? DEFAULT_LANGUAGE
                });
                setIsSuccess(true);
            } catch (e) {
                setIsSuccess(false);
            }
            setIsLoading(false);
        }
    });

    useEffect(() => {
        passwordForm.setFieldValue("currentPassword", "current password");
    }, [passwordForm.initialValues]);

    const passwordLevels = [
        "",
        translate("common.password.strength.very_weak", "Very Weak"),
        translate("common.password.strength.weak", "Weak"),
        translate("common.password.strength.medium", "Medium"),
        translate("common.password.strength.good", "Good"),
        translate("common.password.strength.very_good", "Very Good")
    ];

    const getCurrentPasswordState = (): number => {
        return passwordForm.dirty
            ? Number(
                passwordLevels.length -
                1 -
                (passwordForm.errors.newPassword?.split("/").length ?? 0)
            )
            : 0;
    };

    const checkRequirement = (label: string) => {
        return passwordForm.errors.newPassword?.split("/").some((lb) => lb === label);
    };

    return (
        <div className="row px-md-31 px-1">
            <div className="col-12">
                <section className="account">
                    <div className="d-flex d-md-none justify-content-start">
                        <Link
                            to={routes.HOME.path}
                            icon="Chevron--left"
                            iconSize={20}
                            label={translate("common.back", "Back")}
                            customCss="gap-02 text-bd2-rg ms-n1 mb-12"
                        />
                    </div>
                    <h1 className="text-h2-smbd mb-3">
                        {translate("common.my_account", "My account")}
                    </h1>
                    <div className="d-flex flex-column gap-4">
                        <section aria-label={translate("account.personal_data", "Personal data")}>
                            <div className="d-flex justify-content-between align-items-center mb-22 flex-wrap gap-12">
                                <h2 className="text-h3-md" id="password_settings">
                                    {translate("account.personal_data", "Personal data")}
                                </h2>
                                <Link
                                    to={routes.CONTACTS.path}
                                    className="d-flex align-items-center gap-03">
                                    <i className="color-green-500 icon icon-24 icon-help"></i>
                                    <span className="color-green-500 text-bd2-bd">
                                        {translate(
                                            "account.change_info_request",
                                            "Need to change personal data information? Contact us"
                                        )}
                                    </span>
                                </Link>
                            </div>
                            <form className="form row gx-5 gy-22">
                                <div className="col-12 col-lg-6">
                                    <InputText
                                        disabled
                                        type="text"
                                        label={translate("account.username", "Username")}
                                        name="firstName"
                                        placeholder={translate(
                                            "account.placeholder.first_name",
                                            "Insert first name"
                                        )}
                                        value={user?.firstName}
                                    />
                                </div>
                                <div className="col-12 col-lg-6 order-2 d-none">
                                    <InputText
                                        disabled
                                        type="text"
                                        label={translate("account.last_name", "Last name")}
                                        name="lastName"
                                        placeholder={translate(
                                            "account.placeholder.last_name",
                                            "Insert last name"
                                        )}
                                        value={user?.lastName}
                                    />
                                </div>
                                <div className="col-12 col-lg-6 order-1">
                                    <InputText
                                        disabled
                                        type="text"
                                        label={translate("common.email", "Email")}
                                        name="email"
                                        placeholder={translate(
                                            "passwordForm.placeholder.email",
                                            "Insert email"
                                        )}
                                        value={user?.email}
                                    />
                                </div>
                                <div className="col-12 col-lg-6 order-3">
                                    <InputText
                                        disabled
                                        type="text"
                                        label={translate("account.job_title", "Job title")}
                                        name="jobTitle"
                                        placeholder={translate(
                                            "account.placeholder.job_title",
                                            "Insert job title"
                                        )}
                                        value={translate(`account.role.${user?.jobTitle.code}`, user?.jobTitle.code ?? "")}
                                    />
                                </div>
                            </form>
                        </section>
                        <hr className="is_sm" />
                        <form
                            className="form"
                            aria-labelledby="password_settings"
                            onSubmit={passwordForm.handleSubmit}>
                            <div className="d-flex justify-content-between align-items-center mb-22 flex-wrap gap-12">
                                <h2 className="text-h3-md" id="password_settings">
                                    {translate("account.password_settings", "Password settings")}
                                </h2>
                                {!settingPassword && (
                                    <Button
                                        icon="edit"
                                        type="button"
                                        variant="outlined"
                                        onClick={() => {
                                            setSettingPassword(true);
                                            passwordForm.resetForm();
                                        }}
                                        label={translate(
                                            "page.login.change_password",
                                            "Change password"
                                        )}
                                    />
                                )}
                                {settingPassword && (
                                    <Button
                                        icon="edit"
                                        type="submit"
                                        variant="outlined"
                                        label={translate(
                                            "account.save_password_settings",
                                            "Save password settings"
                                        )}
                                    />
                                )}
                            </div>
                            <div className="row gx-5 gy-22">
                                <div className="col-12">
                                    <InputText
                                        name="currentPassword"
                                        label={translate("common.password", "Password")}
                                        placeholder={translate(
                                            "passwordForm.placeholder.password",
                                            "Insert password"
                                        )}
                                        type="password"
                                        onChange={passwordForm.handleChange}
                                        value={passwordForm.values.currentPassword}
                                        disabled={!settingPassword}
                                        errors={
                                            passwordForm.submitCount > 0
                                                ? passwordForm.errors.currentPassword?.split("/")
                                                : []
                                        }
                                    />
                                </div>
                                {settingPassword && (
                                    <>
                                        <div className="col-12 col-lg-6">
                                            <InputText
                                                name="newPassword"
                                                label={translate(
                                                    "common.new_password",
                                                    "New password"
                                                )}
                                                placeholder={translate(
                                                    "passwordForm.placeholder.new_password",
                                                    "Insert new password"
                                                )}
                                                type="password"
                                                customCSS={
                                                    passwordForm.errors.newPassword
                                                        ? "has_error"
                                                        : ""
                                                }
                                                currentStep={getCurrentPasswordState()}
                                                totalSteps={
                                                    passwordForm.errors.newPassword
                                                        ? passwordLevels.length - 1
                                                        : undefined
                                                }
                                                stepLabel={
                                                    passwordLevels[getCurrentPasswordState()]
                                                }
                                                onChange={passwordForm.handleChange}
                                                value={passwordForm.values.newPassword}
                                            />
                                            {passwordForm.errors.newPassword && (
                                                <div className="form_field mt-1">
                                                    <ul className="form_field-requirements">
                                                        {passwordRequirements.map(
                                                            (req, i) =>
                                                                req.display && (
                                                                    <li
                                                                        className="text-bd3-md d-flex gap-02 align-items-start"
                                                                        key={i}>
                                                                        <i
                                                                            className={`icon icon-16 ${checkRequirement(
                                                                                req.label
                                                                            )
                                                                                    ? "icon-Misuse color-red-600"
                                                                                    : "icon-checkmark--filled color-green-600"
                                                                                }`}></i>
                                                                        {req.label}
                                                                    </li>
                                                                )
                                                        )}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                        <div className="col-12 col-lg-6">
                                            <InputText
                                                type="password"
                                                label={translate(
                                                    "passwordForm.confirm_password",
                                                    "Confirm password"
                                                )}
                                                name="confirmNewPassword"
                                                placeholder={translate(
                                                    "passwordForm.placeholder.confirm_password",
                                                    "Confirm password"
                                                )}
                                                errors={
                                                    passwordForm.submitCount > 0
                                                        ? passwordForm.errors.confirmNewPassword?.split(
                                                            "/"
                                                        )
                                                        : []
                                                }
                                                onChange={passwordForm.handleChange}
                                                value={passwordForm.values.confirmNewPassword}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        </form>
                        <hr className="is_sm" />
                        <section aria-labelledby="privacy" className="pb-22">
                            <h2 className="text-h3-md mb-22" id="privacy">
                                {translate("account.privacy_and_security", "Privacy and security")}
                            </h2>
                            <div className="row g-12">
                                <article className="col-12 col-lg-6 d-flex gap-02 flex-column">
                                    <h3 className="text-bd1-rg color-neutrals-black">
                                        {translate("legal.title.collected_data", "Collected data")}
                                    </h3>
                                    <p className="m-0 text-bd2-rg color-neutrals-dark">
                                        {translate(
                                            "legal.detail.collected_data",
                                            `Our app collects data to improve user experience, including
                                        usage patterns, preferences, and device information.`
                                        )}
                                    </p>
                                </article>
                                <article className="col-12 col-lg-6 d-flex gap-02 flex-column">
                                    <h3 className="text-bd1-rg color-neutrals-black">
                                        {translate("legal.title.anonymity", "Anonymity")}
                                    </h3>
                                    <p className="m-0 text-bd2-rg color-neutrals-dark">
                                        {translate(
                                            "legal.detail.collected_data",
                                            `Our app collects data to improve user experience, including
                                        usage patterns, preferences, and device information.`
                                        )}
                                    </p>
                                </article>
                                <article className="col-12 col-lg-6 d-flex gap-02 flex-column">
                                    <h3 className="text-bd1-rg color-neutrals-black">
                                        {translate(
                                            "legal.title.sharing_information",
                                            "Sharing information"
                                        )}
                                    </h3>
                                    <p className="m-0 text-bd2-rg color-neutrals-dark">
                                        {translate(
                                            "legal.detail.collected_data",
                                            `Our app collects data to improve user experience, including
                                        usage patterns, preferences, and device information.`
                                        )}
                                    </p>
                                </article>
                                <div className="col-12 col-lg-6 d-flex gap-02 flex-column align-items-start">
                                    <Link
                                        to={routes.LEGAL.path}
                                        target="_blank"
                                        icon="ungroup"
                                        isIconRight
                                        label={translate(
                                            "legal.link.more_info",
                                            "More information here"
                                        )}
                                        customCss="ms-n1 text-bd2-bd"
                                    />
                                </div>
                            </div>
                        </section>
                    </div>
                </section>
            </div>
        </div>
    );
}

export default Account;
